<template>
  <div class="wd-my-3">
    <template v-if="!hideHead">
      <div v-if="remark.title" :class="remark.subtitle ? 'wd-mb-1' : 'wd-mb-2'">
        <div class="wd-text-sm wd-font-normal" :class="hasFieldFailed ? 'wd-text-red50' : 'wd-text-black50'">
          {{ remark.title }}<span v-if="isFieldOptional"> ({{ $t('optional') }})</span>
        </div>
      </div>
      <template v-if="subtitle">
        <div v-if="remark.type === RemarkInputType.Description" class="wd-text-gray80 wd-mb-7" v-html="subtitle"></div>
        <div v-else class="wd-text-gray80 wd-mb-2">
          {{ subtitle }}
        </div>
      </template>
    </template>
    <!-- 文字輸入框 -->
    <div v-if="remark.type === RemarkInputType.Text" class="wd-mb-3">
      <Field
        ref="fieldRef"
        :key="remark.title"
        v-slot="{ field, errorMessage, handleChange }"
        v-model.trim="fieldValue"
        :name="`${remark.name || remark.id}`"
        :rules="formFieldRules"
      >
        <v-text-field
          :model-value="field.value"
          :placeholder="remark.placeholder"
          color="orange50"
          :max-height="40"
          :error-messages="errorMessage"
          variant="outlined"
          density="compact"
          hide-details="auto"
          @update:model-value="handleChange"
        />
      </Field>
    </div>

    <!-- 日期輸入框 -->
    <div v-if="remark.type === RemarkInputType.DateSelector" class="wd-mb-3">
      <v-menu v-model="menuValue" :close-on-content-click="false" transition="scale-transition" max-width="290px" min-width="290px">
        <template #activator="{ props }">
          <Field
            ref="fieldRef"
            :key="remark.title"
            v-slot="{ field, errorMessage }"
            :model-value="dateFormatted"
            :name="`${remark.name || remark.id}`"
            :rules="formFieldRules"
          >
            <v-text-field
              :model-value="field.value"
              :placeholder="remark.placeholder"
              color="orange50"
              persistent-hint
              variant="outlined"
              density="compact"
              readonly
              :error-messages="errorMessage"
              hide-details="auto"
              v-bind="props"
            />
          </Field>
        </template>

        <v-date-picker
          class="remark-date-picker"
          :model-value="dateFormatted ? new Date(dateFormatted) : undefined"
          default
          color="orange50"
          hide-header
          @update:model-value="inputDatePicker"
        />
      </v-menu>
    </div>
    <!-- 日期時間輸入框 -->
    <div v-if="remark.type === RemarkInputType.DateTimeSelector" class="wd-mb-3">
      <Field
        ref="fieldRef"
        :key="remark.title"
        v-slot="{ errorMessage, field, meta, handleChange }"
        v-model="fieldValue"
        :name="`${remark.name || remark.id}`"
        :rules="formFieldRules"
      >
        <VueCtkDateTimePicker
          id="remark-date-time"
          ref="dateTimePicker"
          :model-value="field.value"
          :class="{ focus: !meta.touched && meta.dirty && !meta.valid, failed: errorMessage }"
          :error="!!errorMessage"
          :locale="locale"
          :label="remark.placeholder"
          hint=" "
          format="YYYY-MM-DD HH:mm"
          formatted="YYYY/MM/DD HH:mm"
          color="#ff5537"
          no-button-now
          no-header
          @update:model-value="handleChange"
        />
        <span v-if="errorMessage" class="wd-text-xs wd-text-red50 wd-px-4">{{ $t(errorMessage) }}</span>
      </Field>
    </div>

    <!-- 單選輸入框 -->
    <div v-if="remark.type === RemarkInputType.Radio" class="wd-mb-3">
      <Field
        ref="fieldRef"
        :key="remark.id"
        v-slot="{ field, errorMessage, handleChange, validate }"
        v-model="fieldValue"
        :name="`radio-${remark.id}`"
        :rules="formFieldRules"
      >
        <RemarkFormRadio
          v-model:option-index="selectedIndex"
          class="wd-mb-7"
          :model-value="field.value"
          :title="remark.title"
          :options="remarkOptions"
          :placeholder="remark.placeholder"
          :error-messages="errorMessage"
          :required="remark.is_required"
          @update:model-value="handleChange"
          @close="validate"
        />
      </Field>
    </div>

    <!-- 複選 input -->
    <div v-if="remark.type === RemarkInputType.Checkbox" class="wd-mb-3">
      <Field
        ref="fieldRef"
        :key="remark.title"
        v-slot="{ field, errorMessage, handleChange, validate }"
        v-model="fieldValue"
        :name="`checkbox-${remark.name || remark.id}`"
        :rules="formFieldRules"
      >
        <RemarkFormCheckbox
          :model-value="field.value"
          :title="remark.title"
          :options="remarkOptions"
          :placeholder="remark.placeholder"
          :error-messages="errorMessage"
          @update:model-value="handleChange"
          @close="validate"
        />
      </Field>
    </div>

    <!-- Tags 選擇 -->
    <div v-if="remark.type === RemarkInputType.Tags" class="wd-mb-3">
      <Field
        ref="fieldRef"
        :key="remark.title"
        v-slot="{ field, errorMessage, handleChange }"
        v-model="fieldValue"
        :name="`tags-${remark.name || remark.id}`"
        :rules="formFieldRules"
      >
        <RemarkFormTags
          :model-value="field.value"
          :options="remark.opt"
          :limit-max="remark.limit_max"
          :custom-input-placeholder="remark.custom_input_placeholder"
          @update:model-value="handleChange"
        />
        <div v-if="errorMessage" class="wd-text-xs wd-text-red50 wd-mt-2">{{ $t(errorMessage) }}</div>
      </Field>
    </div>

    <!-- 數字輸入框 -->
    <div v-if="remark.type === RemarkInputType.Quantity" class="wd-mb-3">
      <Field
        ref="fieldRef"
        :key="remark.title"
        v-slot="{ field, errorMessage, handleChange }"
        v-model="fieldValue"
        :name="`number-${remark.name || remark.id}`"
        mode="eager"
        :rules="formFieldRules"
      >
        <div class="wd-mb-7">
          <RemarkFormNumber :model-value="field.value" :options="remarkOptions" :error-message="errorMessage" @update:model-value="handleChange" />
        </div>
      </Field>
    </div>

    <!-- 國碼 電話號碼 -->
    <div v-if="remark.type === RemarkInputType.NationalPhoneNumber" ref="wrapRef" class="wd-relative wd-mb-3">
      <Field
        ref="fieldRef"
        v-slot="{ field, handleChange, errorMessage }"
        v-model="fieldValue"
        mode="lazy"
        :name="`phone-${remark.name || remark.id}`"
        :rules="formFieldRules"
        :debounce="200"
      >
        <PhoneNumberInput
          v-model:option-index="selectedIndex"
          :model-value="field.value"
          :options="remarkOptions"
          :placeholder="remark.placeholder"
          :error-messages="errorMessage"
          hide-details="auto"
          @update:model-value="handleChange"
        />
      </Field>
    </div>

    <!-- 圖片說明 -->
    <div v-if="remark.type === RemarkInputType.Image" class="wd-mb-7">
      <div class="wd-w-full">
        <v-img min-height="150" :src="imageURL">
          <template #placeholder>
            <div>
              <v-skeleton-loader max-width="100%" type="card"></v-skeleton-loader>
            </div>
          </template>
        </v-img>
      </div>
    </div>
  </div>
</template>

<script>
import { useElementSize } from '@vueuse/core'
import { useToggle, watchDebounced } from '@vueuse/shared'
import VueCtkDateTimePicker from 'vue-ctk-date-time-picker'
import 'vue-ctk-date-time-picker/dist/vue-ctk-date-time-picker.css'
import * as Yup from 'yup'

import { RemarkInputType, RemarkIdEnum } from '@/view-models/remark'
import RemarkFormRadio from '@/components/RemarkFormRadio.vue'
import RemarkFormCheckbox from '@/components/RemarkFormCheckbox.vue'
import RemarkFormNumber from '@/components/RemarkFormNumber.vue'
import PhoneNumberInput from '@/components/PhoneNumberInput.vue'
import RemarkFormTags from '@/components/RemarkFormTags.vue'
import { timeUtil } from '@/utils/time'
import utils from '@/utils'

export default defineComponent({
  components: {
    VueCtkDateTimePicker,
    RemarkFormRadio,
    RemarkFormCheckbox,
    RemarkFormNumber,
    PhoneNumberInput,
    RemarkFormTags,
  },
  props: {
    /** for v-model 綁 form field data 給 parent
     * @type {PropType<string | number | FormFieldOptionCreationData[]>}
     */
    modelValue: [String, Number, Array],
    remark: {
      type: Object,
      default: () => ({
        id: 0,
        type: 0,
        title: '',
        content: '',
        placeholder: '',
        /**
         * {
         *   id: 0,
         *   content: '',
         * }
         */
        last_input: null,
        opt_default_idx: null,
        opt: [],
        error_msg: '',
      }),
    },
    lastAnswerOption: {
      type: [Number, Array],
    },
    defaultOptionRequired: {
      type: Boolean,
      default: false,
    },
    hideMessage: {
      type: Boolean,
      default: false,
    },
    hideHead: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['update:modelValue', 'optionChange'],
  setup(props, ctx) {
    const nuxtApp = useNuxtApp()
    const fieldRef = useTemplateRef('fieldRef')
    const wrapRef = ref(null)
    const { width: wrapWidth } = useElementSize(wrapRef)
    const rules = useFieldRules()
    const { getRuleByType } = useRemarkStrategy(props.remark)

    const hasFieldFailed = computed(() => !!fieldRef.value?.errorMessage)
    const isMultiValueMode = computed(
      () =>
        props.remark.type === RemarkInputType.Quantity || props.remark.type === RemarkInputType.Checkbox || props.remark.type === RemarkInputType.Tags
    )
    const isDateValueMode = computed(
      () => props.remark.type === RemarkInputType.DateSelector || props.remark.type === RemarkInputType.DateTimeSelector
    )

    const singleFieldValue = ref('')
    const dateFieldValue = ref(null)
    const multiFieldValue = ref([])
    const fieldValue = computed({
      get() {
        if (isMultiValueMode.value) {
          return multiFieldValue.value
        }
        if (isDateValueMode.value) {
          return dateFieldValue.value
        }
        return singleFieldValue.value
      },
      set(value) {
        if (isMultiValueMode.value) {
          multiFieldValue.value = value ?? []
        } else if (isDateValueMode.value) {
          dateFieldValue.value = value ?? null
        } else {
          singleFieldValue.value = value ?? ''
        }
      },
    })

    syncRefs(() => props.modelValue, fieldValue)

    const dateFormatted = computed(() => (fieldValue.value ? timeUtil.formatDate(fieldValue.value) : ''))
    const [menuValue] = useToggle()

    const lastOption = computed(() => props.remark.opt.findIndex(option => option.id === props.lastAnswerOption))
    const selectedIndex = ref(lastOption.value !== -1 ? lastOption.value : props.remark.opt_default_idx ?? void 0)

    // 監聽 opt_default_idx 的變化,更新 selectedIndex
    watch(
      () => props.remark.opt_default_idx,
      newIdx => {
        if (newIdx !== null && newIdx !== undefined && !fieldRef.value?.meta?.dirty) {
          selectedIndex.value = newIdx
        }
      }
    )

    const isFieldOptional = computed(
      () =>
        !props.remark.ruleOption?.required &&
        props.remark.type !== RemarkInputType.Description &&
        props.remark.type !== RemarkInputType.Image &&
        props.remark.type !== RemarkInputType.Quantity
    )

    const skipLastInputField = computed(
      () => ![RemarkInputType.Text, RemarkInputType.NationalPhoneNumber, RemarkInputType.Tags].includes(props.remark.type)
    )

    const locale = computed(() => (nuxtApp.$i18n.localeProperties.value.language || 'en-US').toLowerCase())

    const selectedOption = computed(() => remarkOptions.value?.[selectedIndex.value] || [])

    /** 前端防止資料設定上的錯誤 不會每個 remark type 都需要 options */
    const hasOptionsTypes = computed(() => [RemarkInputType.Radio, RemarkInputType.NationalPhoneNumber].includes(props.remark.type))

    const imageURL = computed(() => utils.getMediaCdnUrl(props.remark.content || ''))
    const subtitle = computed(() => (props.remark.type === RemarkInputType.Description ? props.remark.content : props.remark.subtitle || ''))

    const formFieldRules = computed(() => {
      const { id, type, title, is_required, ruleOption } = props.remark

      // 優先使用 ruleOption.schema（如果有提供）
      if (ruleOption?.schema) {
        return ruleOption.schema
      }

      let tmpRule = Yup.string()

      if (id === RemarkIdEnum.EMAIL) {
        tmpRule = rules.email()
      } else {
        tmpRule = getRuleByType(type)
      }
      if (title) tmpRule = tmpRule.label(title)
      if (!is_required) {
        return tmpRule
          .notRequired()
          .nullable()
          .transform(value => (value === '' ? null : value))
      }

      return tmpRule
    })

    /**
     * @ref https://github.com/funnow-docs/funnow-api-doc/blob/9ceb9a7703352c3e99a1ffe5ddb91836acce58b3/docs/product/booking_remark.md
     */
    const remarkOptions = computed(() =>
      props.remark.opt.map(opt => ({
        ...opt,
        label: opt.value,
        input_value: {
          sub_id: opt.id,
          content: '',
        },
      }))
    )

    /** apply last input */
    const stopWatchLastInput = watchDebounced(
      () => props.remark.last_input,
      lastInput => {
        if (fieldRef.value?.meta?.dirty || !lastInput || skipLastInputField.value) {
          return
        }
        if (Array.isArray(lastInput) && isMultiValueMode.value) {
          const normalized = lastInput
            .map(item => {
              const subId = item?.id ?? item?.sub_id
              if (!subId) {
                return null
              }
              return {
                sub_id: subId,
                content: item?.content ?? item?.value ?? '',
              }
            })
            .filter(Boolean)
          fieldValue.value = normalized
          stopWatchLastInput()
          return
        }
        // has options
        if (remarkOptions.value.length) {
          const lastIndex = remarkOptions.value.findIndex(opt => opt.id === lastInput.id)
          selectedIndex.value = lastIndex >= 0 ? lastIndex : props.remark.opt_default_idx
        }
        fieldValue.value = lastInput.content || ''
        stopWatchLastInput()
      },
      { debounce: 200, deep: true, immediate: true }
    )

    watchDebounced(
      selectedOption,
      option => {
        if (!hasOptionsTypes.value) {
          return
        }
        ctx.emit('optionChange', [props.remark.id, option.id])
      },
      { debounce: 300, immediate: true }
    )

    // for 更新 emit array field value 出去
    watchDebounced(
      fieldValue,
      value => {
        if (value === props.modelValue) {
          return
        }
        ctx.emit('update:modelValue', value)
      },
      { debounce: 50, deep: true, immediate: false }
    )

    onMounted(() => {
      fieldValue.value = props.modelValue || fieldValue.value
    })

    function inputDatePicker(value) {
      menuValue.value = false
      fieldValue.value = nuxtApp.$dayjs(value).format('YYYY/MM/DD')
    }

    return {
      isFieldOptional,
      fieldValue,
      multiFieldValue,
      dateFormatted,
      remarkOptions,
      RemarkInputType,
      inputDatePicker,
      selectedOption,
      selectedIndex,
      menuValue,
      locale,
      wrapRef,
      wrapWidth,
      hasFieldFailed,
      imageURL,
      subtitle,
      formFieldRules,
      lastOption,
    }
  },
})
</script>

<style lang="scss">
.v-menu__content {
  // override default max-width: 80%;
  max-width: 100%;
}
.v-application {
  .error--text {
    @apply wd-text-red50 wd-caret-red50 #{!important};
  }
}

.remark-date-picker {
  .v-picker__body {
    .v-date-picker-controls {
      > .v-btn--icon {
        display: block !important;
      }
    }
  }
}

#remark-date-time-wrapper {
  #remark-date-input,
  #remark-date-time-input {
    padding: 12px !important;
    font-size: 15px;
    &:hover {
      @apply wd-border-black;
    }
    @apply wd-border-gray50;
    &.focus {
      @apply wd-border-orange50;
      // #remark-date-input,
      // #remark-date-time-input {
      // }
    }
    &.failed {
      @apply wd-border-red50;
      // &#remark-date-input,
      // &#remark-date-time-input {
      // }
    }
  }
}
</style>
